export const normalizeProjectedStatKey = (statKey) => {
  if (typeof statKey !== "string") return statKey;
  return statKey
    .replace(/^goblin_/, "projected_")
    .replace(/^demon_/, "projected_")
    .replace(
      /^(projected_|promo_)?(?:three_pointers_made|three_points_made|threes_made|made_threes)$/,
      (_, prefix = "") => `${prefix}3ptm`
    )
    .replace(/plus\/minus$/, "plus_minus");
};

const LOW_LIQUIDITY_STAT_FAMILIES = {
  nhl: [
    { key: "goalie_saves", label: "Goalie Saves", aliases: ["goalie_saves", "saves"] },
    { key: "shots_on_goal", label: "Shots on Goal", aliases: ["shots_on_goal", "shots", "sog"] },
    { key: "blocked_shots", label: "Blocked Shots", aliases: ["blocked_shots"] },
    { key: "shots_against", label: "Shots Against", aliases: ["shots_against"] },
    { key: "goals_allowed", label: "Goals Allowed", aliases: ["goals_allowed", "goals_against"] },
    {
      key: "minutes",
      label: "Minutes",
      aliases: ["minutes", "minutes_played", "minutesplayed", "time_on_ice", "timeonice", "toi"],
    },
  ],
  mlb: [
    { key: "total_bases", label: "Total Bases", aliases: ["total_bases", "tb"] },
    {
      key: "pitching_hits_allowed",
      label: "Pitching Hits Allowed",
      aliases: ["pitching_hits_allowed", "hits_allowed", "pitching_hits"],
    },
    { key: "pitching_walks", label: "Pitching Walks", aliases: ["pitching_walks"] },
    { key: "pitching_earned_runs", label: "Pitching Earned Runs", aliases: ["pitching_earned_runs"] },
    {
      key: "pitching_strikeouts",
      label: "Pitching Strikeouts",
      aliases: ["pitching_strikeouts", "strikeouts"],
    },
    {
      key: "total_outs_pitched",
      label: "Pitcher Outs",
      aliases: ["total_outs_pitched", "pitching_outs", "pitch_outs", "po"],
    },
  ],
  soccer: [
    { key: "goalie_saves", label: "Goalie Saves", aliases: ["goalie_saves", "saves"] },
    {
      key: "shots_on_goal",
      label: "Shots on Goal",
      aliases: ["shots_on_goal", "shots_on_target", "sot", "sog"],
    },
    { key: "passes_attempted", label: "Passes Attempted", aliases: ["passes_attempted"] },
    { key: "clearances", label: "Clearances", aliases: ["clearances"] },
  ],
};

const LOW_LIQUIDITY_LEAGUE_ALIASES = {
  ohockey: "nhl",
  mls: "soccer",
  epl: "soccer",
  premier_league: "soccer",
  laliga: "soccer",
  seriea: "soccer",
  ligue1: "soccer",
  worldcup: "soccer",
  worldcup1h: "soccer",
  worldcup2h: "soccer",
};

const SOCCER_PROTECTED_PROP_FAMILY_STATS = new Set([
  "passes_attempted",
  "shots",
  "shots_on_goal",
  "clearances",
]);
const SOCCER_PROTECTED_PROP_FAMILY_LABEL = "Passes Attempted, Shots, Shots on Goal, and Clearances";

const DEFAULT_LOW_LIQUIDITY_STAT_KEYS = [
  "goalie_saves",
  "shots_on_goal",
  "blocked_shots",
  "shots_against",
  "goals_allowed",
  "total_bases",
  "pitching_hits_allowed",
  "pitching_walks",
  "pitching_earned_runs",
  "pitching_strikeouts",
  "total_outs_pitched",
  "passes_attempted",
  "clearances",
];

const getConfiguredLowLiquidityStatKeys = () => {
  if (typeof document === "undefined") {
    return DEFAULT_LOW_LIQUIDITY_STAT_KEYS;
  }

  const input = document.getElementById("ww-low-liquidity-rule-stats");
  if (!input) {
    return DEFAULT_LOW_LIQUIDITY_STAT_KEYS;
  }

  try {
    const parsed = JSON.parse(input.value || "[]");
    if (!Array.isArray(parsed)) {
      return DEFAULT_LOW_LIQUIDITY_STAT_KEYS;
    }

    return [...new Set(
      [...parsed, "clearances"]
        .map((key) => String(key || "").trim().toLowerCase())
        .filter(Boolean)
    )];
  } catch {
    return DEFAULT_LOW_LIQUIDITY_STAT_KEYS;
  }
};

const normalizeLowLiquidityLeague = (league) => {
  const normalizedLeague = typeof league === "string"
    ? league.trim().toLowerCase()
    : "";

  return LOW_LIQUIDITY_LEAGUE_ALIASES[normalizedLeague] || normalizedLeague;
};

const normalizeLowLiquidityStatKey = (statKey) => {
  if (typeof statKey !== "string") {
    return "";
  }

  return normalizeProjectedStatKey(statKey)
    .replace(/^(projected_|promo_)/, "")
    .trim()
    .toLowerCase();
};

const isSoccerProtectedPropFamilySelection = (entry) => {
  const league = normalizeLowLiquidityLeague(entry?.league || entry?.sport);
  const statKey = normalizeLowLiquidityStatKey(entry?.stat_key || entry?.stat);
  return league === "soccer" && SOCCER_PROTECTED_PROP_FAMILY_STATS.has(statKey);
};

export const getLowLiquidityStatDetails = (league, statKey) => {
  const normalizedLeague = normalizeLowLiquidityLeague(league);
  const normalizedStatKey = normalizeLowLiquidityStatKey(statKey);
  const configuredKeys = new Set(getConfiguredLowLiquidityStatKeys());

  if (!normalizedLeague || !normalizedStatKey) {
    return null;
  }

  const families = LOW_LIQUIDITY_STAT_FAMILIES[normalizedLeague] || [];
  return families.find((family) =>
    configuredKeys.has(family.key) && family.aliases.includes(normalizedStatKey)
  ) || null;
};

const getCurrentUserTier = (userTier) => {
  const parsedTier = Number(userTier);
  if (userTier !== null && userTier !== undefined && Number.isFinite(parsedTier)) {
    return parsedTier;
  }

  if (typeof document === "undefined") {
    return null;
  }

  const boardTier = Number(document.getElementById("user-tier")?.value);
  return Number.isFinite(boardTier) ? boardTier : null;
};

export const getLowLiquidityLineupLimitViolation = (selected, userTier = null, isAuthenticated = true) => {
  // Guest lineup building has no account policy yet; submission stays strict.
  if (!isAuthenticated) return null;
  const entries = Array.isArray(selected) ? selected : [];
  let lowLiquidityCount = 0;
  const familyCounts = new Map();

  entries.forEach((entry) => {
    const details = getLowLiquidityStatDetails(entry?.league, entry?.stat_key);
    if (!details) {
      return;
    }

    lowLiquidityCount += 1;
    familyCounts.set(details.key, {
      count: (familyCounts.get(details.key)?.count || 0) + 1,
      label: details.label,
    });
  });

  const soccerProtectedFamilyCount = entries.filter(isSoccerProtectedPropFamilySelection).length;

  const isTierZero = getCurrentUserTier(userTier) === 0;
  const soccerProtectedFamilyMaximum = isTierZero
    ? Math.ceil(entries.length / 2)
    : Math.floor(entries.length / 2);
  if (entries.length >= 2 && soccerProtectedFamilyCount > soccerProtectedFamilyMaximum) {
    return {
      code: "soccer_protected_prop_family_entry_limit",
      message: isTierZero
        ? `This lineup allows a maximum of ${soccerProtectedFamilyMaximum} soccer pick${soccerProtectedFamilyMaximum === 1 ? "" : "s"} from these stats: ${SOCCER_PROTECTED_PROP_FAMILY_LABEL}. Add picks from other stats or sports to increase this limit.`
        : `Protected soccer props (${SOCCER_PROTECTED_PROP_FAMILY_LABEL}) may not make up more than 50% of an entry. This lineup allows a maximum of ${soccerProtectedFamilyMaximum}.`,
    };
  }

  for (const { count, label } of familyCounts.values()) {
    if (count > 3) {
      return {
        code: "low_liquidity_same_stat_limit",
        message: `3 max ${label} selections allowed in lineup.`,
      };
    }
  }

  if (lowLiquidityCount > 3) {
    return {
      code: "low_liquidity_total_limit",
      message: "Three max low-liquidity picks in one lineup.",
    };
  }

  return null;
};

/**
 * Validate an in-progress selection change without trapping a customer in an
 * already-invalid protected-soccer majority. A newly added non-protected pick
 * is allowed to dilute that majority even when one more pick will still be
 * needed before the lineup becomes eligible. Final submission continues to
 * use getLowLiquidityLineupLimitViolation() and remains strict.
 */
export const getLowLiquiditySelectionChangeViolation = (currentSelected, nextSelected, userTier = null, isAuthenticated = true) => {
  const violation = getLowLiquidityLineupLimitViolation(nextSelected, userTier, isAuthenticated);
  if (!violation || violation.code !== "soccer_protected_prop_family_entry_limit") {
    return violation;
  }

  const currentEntries = Array.isArray(currentSelected) ? currentSelected : [];
  const nextEntries = Array.isArray(nextSelected) ? nextSelected : [];
  const currentProtectedCount = currentEntries.filter(isSoccerProtectedPropFamilySelection).length;
  const nextProtectedCount = nextEntries.filter(isSoccerProtectedPropFamilySelection).length;
  const isDilutingAddition =
    nextEntries.length > currentEntries.length &&
    nextProtectedCount === currentProtectedCount;

  return isDilutingAddition ? null : violation;
};

export const getSelectionStateFromToken = (promoToken) => {
  const token = typeof promoToken === "string"
    ? promoToken
    : promoToken === true
      ? "promo"
      : "";

  if (token === "easy") {
    return { easy: true, hard: false, promo: false };
  }

  if (token === "hard") {
    return { easy: false, hard: true, promo: false };
  }

  if (token === "promo" || token === "true") {
    return { easy: false, hard: false, promo: true };
  }

  return { easy: false, hard: false, promo: false };
};

export const getCardSelectionState = (statKey, basePromo = false, easy = false, hard = false) => {
  const rawStatKey = typeof statKey === "string" ? statKey : "";
  const normalizedStatKey = normalizeProjectedStatKey(rawStatKey);
  const isEasy = rawStatKey.startsWith("goblin_") || Boolean(easy);
  const isHard = !isEasy && (rawStatKey.startsWith("demon_") || Boolean(hard));
  const isPromo = !isEasy && !isHard && Boolean(basePromo);

  return {
    statKey: normalizedStatKey,
    easy: isEasy,
    hard: isHard,
    promo: isPromo,
    promoToken: isEasy ? "easy" : isHard ? "hard" : isPromo ? "promo" : "",
    isStandard: !isEasy && !isHard && !isPromo,
  };
};

export const matchesSelectionFamily = (candidate, athleteId, statKey) => {
  if (!candidate || candidate.athlete_id != athleteId) {
    return false;
  }

  return normalizeProjectedStatKey(candidate.stat_key) === normalizeProjectedStatKey(statKey);
};

export const matchesSelectionVariant = (
  candidate,
  athleteId,
  statKey,
  promoToken = false,
  easy = false,
  hard = false
) => {
  if (!matchesSelectionFamily(candidate, athleteId, statKey)) {
    return false;
  }

  const candidateState = getCardSelectionState(
    candidate.stat_key,
    candidate.promo,
    candidate.easy,
    candidate.hard
  );
  const requestedState = getCardSelectionState(statKey, promoToken, easy, hard);

  return (
    candidateState.easy === requestedState.easy &&
    candidateState.hard === requestedState.hard &&
    candidateState.promo === requestedState.promo
  );
};
